import { Component, OnInit, input, output, viewChild, TemplateRef, DestroyRef, inject, signal, computed, WritableSignal, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { BulkErrorUpdateScope } from 'src/app/models/bulk-error-update-scope';
import { ClientInformation } from 'src/app/models/client-information';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { ErrorCategory } from 'src/app/models/error-category';
import { ImportType } from 'src/app/models/import-type';
import { Integration } from 'src/app/models/integration';
import { IntegrationError, EnrichedIntegrationError } from 'src/app/models/integration-error';
import { ReportErrorSearchFilter } from 'src/app/models/report-error-search-filter';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridSort } from 'src/app/features/grid/classes/grid-config.types';
import { LTFilterRule } from 'src/app/features/filter/models/lt-filter.models';
import { IntegrationErrorsService } from 'src/app/services/integration-errors.service';
import { LTFilterService } from 'src/app/features/filter/services/lt-filter.service';
import { ToastService } from 'src/app/services/toast.service';
import { GridTemplateDirective } from 'src/app/features/grid/classes/grid-template.directive';
import { LTButtonComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-button/lt-button.component';
import { LTCurrentStatusToggleComponent, StatusType } from 'src/app/features/lt-errors/components/lt-current-status-toggle/lt-current-status-toggle';
import { LTErrorBulkUpdateDialogComponent } from '../lt-error-bulk-update-dialog/lt-error-bulk-update-dialog';
import { LTErrorDetailViewComponent } from '../lt-error-detail-view/lt-error-detail-view';
import { LTErrorUpdateDialogComponent } from '../lt-error-update-dialog/lt-error-update-dialog';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';
import { LTHelperUtils } from 'src/app/utilities/lt-helper.utils';
import { StringUtils } from 'src/app/utilities/string.utils';

@Component({
  selector: 'app-lt-errors-grid',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    GridTemplateDirective,
    LTButtonComponent,
    LTCurrentStatusToggleComponent,
    LTErrorDetailViewComponent,
    LTPngTableComponent,
    LTSpinnerComponent,
  ],
  templateUrl: './lt-errors-grid.html',
  styleUrl: './lt-errors-grid.scss',
  standalone: true,
  providers: [IntegrationErrorsService, LTFilterService],
})
export class LTErrorsGridComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  updateErrorStatusDialog = viewChild<TemplateRef<any>>('updateErrorStatusDialog');
  bulkUpdateDialog = viewChild<TemplateRef<any>>('bulkUpdateDialog');
  pngTableComponent = viewChild<LTPngTableComponent<IntegrationError>>('table');

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly errorService = inject(IntegrationErrorsService);
  private readonly toastService = inject(ToastService);
  private readonly filterService = inject(LTFilterService);

  //#region Inputs-Outputs
  data = input<IntegrationError[]>();
  clients = input<ClientInformation[]>();
  categories = input<ErrorCategory[]>();
  statuses = input<CurrentStatus[]>();
  destinations = input<DestinationEndpoint[]>();
  importTypes = input<ImportType[]>();
  loading = input<boolean>(); // show loading icon from parent
  sorts = input<GridSort[]>();
  integrationsMap = input<Map<string, Integration>>(); // ask parent for updated data

  errorUpdated = output<IntegrationError[]>();
  //#endregion Inputs-Outputs

  //#region status signals
  isUpdating = signal(false);
  statusUpdating = signal<boolean>(false);
  //#endregion

  //#region selection variables
  selectedError = signal<IntegrationError>(null);
  private selectedErrors: WritableSignal<IntegrationError[]> = signal<IntegrationError[]>([]);

  // Computed signals for derived state
  hasSelectedErrors = computed(() => this.selectedErrors().length > 0);
  selectedErrorCount = computed(() => this.selectedErrors().length);
  //#endregion

  //#region error status variables
  private statusSearchTerm: WritableSignal<string> = signal<string>('');
  hasStatusFilter = signal<boolean>(false);
  //#endregion

  //#region report filter - set for Integration Error Report Page
  currentReportFilter = signal<ReportErrorSearchFilter>(null);
  //#endregion

  //#region PNG-Vars
  errorsGrid = signal<GridSettings>(null);
  errorsGridReady = signal<boolean>(false);
  gridLoading = signal<boolean>(false);

  errorColumns: GridColumnData[] = [
    new GridColumnData({ field: 'integration.dataSourceTypeId', header: 'Data Source Type', dataType: 'text', type: 'text', display: false, exportable: true, canHide: false }),
    new GridColumnData({ width: '250px', field: 'statusName', header: 'Status Name', dataType: 'text', type: 'text', display: true, exportable: false }),
    new GridColumnData({ width: '200px', field: 'receivedDate', header: 'Last Received', dataType: 'date', type: 'date', format: 'MM/dd/yyyy, h:mm:ss a', display: true, exportable: true, filterable: false, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ 
      width: '350px', 
      field: 'integrationName', 
      header: 'Integration Name', 
      exportHeader: 'Integration', 
      dataType: 'text', 
      type: 'text', 
      display: true, 
      exportable: true, 
      displayFunction: (value: any, rowData: EnrichedIntegrationError) => rowData.integrationDisplayName,
      exportFunction: (value: any, rowData: EnrichedIntegrationError) => rowData.integrationDisplayName,
      bodyStyleClass: 'text-wrap',
    }),
    new GridColumnData({ width: '500px', field: 'logMessage', header: 'Log Message', dataType: 'text', type: 'text', display: true, exportable: true, pipeFormat: 'truncate:200', bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '120px', field: 'serverId', header: 'Server Id', dataType: 'text', type: 'text', display: true, exportable: true, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '100px', field: 'criticalError', header: 'Critical', dataType: 'boolean', type: 'boolean', display: true, exportable: false }),
    new GridColumnData({ width: '200px', field: 'endpointName', header: 'Destination', dataType: 'text', type: 'text', display: true, exportable: false, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '120px', field: 'errorCount', header: '#', exportHeader: 'Error Count', dataType: 'number', type: 'number', display: true, exportable: true }),
    new GridColumnData({ 
      width: '200px', 
      field: 'typeDescription', 
      header: 'Import Type', 
      dataType: 'text', 
      type: 'text', 
      display: true, 
      exportable: false,
      bodyStyleClass: 'text-wrap',
    }),
    new GridColumnData({ 
      width: '200px', 
      field: 'categoryName', 
      header: 'Category-Priority', 
      exportHeader: 'Category', 
      dataType: 'text', 
      type: 'text', 
      displayFunction: (value: any, rowData: any) => `${rowData.categoryName}-${rowData.categoryPriority}`,
      exportFunction: (value: any, rowData: any) => rowData.categoryName,
      display: true, 
      exportable: true,
      bodyStyleClass: 'text-wrap',
    }),
    new GridColumnData({ width: '200px', field: 'initialErrorDate', header: 'First Received', dataType: 'date', type: 'date', format: 'MM/dd/yyyy, h:mm:ss a', display: true, exportable: true, bodyStyleClass: 'text-wrap', canFreeze: false, canReorder: false }),
    new GridColumnData({ field: 'locked', header: 'Locked', dataType: 'boolean', type: 'boolean', display: false, exportable: true, canHide: false, canFreeze: false, canReorder: false }),
    new GridColumnData({ field: 'resolutionDate', header: 'Resolution Date', dataType: 'date', type: 'date', format: 'MM/dd/yyyy, h:mm:ss a', display: false, exportable: true, canHide: false, canFreeze: false, canReorder: false }),
    new GridColumnData({ field: 'resolutionComment', header: 'Resolution Comment', dataType: 'text', type: 'text', display: false, exportable: true, canHide: false, canFreeze: false, canReorder: false }),
  ];

  //#endregion
  
  //#region data utils
  gridData = computed(() => {
    return this.filteredData();
  });

  filteredData = computed(() => {
    const data = this.enrichedData();
    const hasFilter = this.hasStatusFilter();
    const filterValue = this.statusSearchTerm();
    const reportFilter = this.currentReportFilter();
    const reportFilterList = this.reportFilters();
    
    if ((!hasFilter || filterValue === null) && reportFilter === null && reportFilterList.length === 0) return data;

    let filtered = data;

    if (hasFilter && filterValue !== null){
      filtered = this.filterService.filterBy(
        filtered, 'statusName', filterValue,
      );
    }
    
    if (reportFilter && reportFilterList.length > 0) {
      const currentReportFilterList = this.filterService.createFilterGroup('and', reportFilterList);
      filtered = this.filterService.applyFilter(filtered, currentReportFilterList);
    }

    return filtered;
  });

  enrichedData = computed(() => {
    const errors = this.data();
    const integrations = this.integrationsMap();
    const clients = this.clients();
    const importTypes = this.importTypes();
    const destinations = this.destinations();
    const statuses = this.statuses();
    const categories = this.categories();

    return errors.map(error => this.enrichErrorWithInformation(
      error, 
      integrations, 
      clients,
      importTypes,
      destinations,
      statuses,
      categories,
    ));
  });

  private enrichErrorWithInformation(
    error: IntegrationError, 
    integrations: Map<string, Integration>, 
    clients: ClientInformation[],
    importTypes: ImportType[],
    destinations: DestinationEndpoint[],
    statuses?: CurrentStatus[],
    categories?: ErrorCategory[],
  ): EnrichedIntegrationError {
    const enrichedError = { ...error } as EnrichedIntegrationError;

    enrichedError.typeDescription = LTHelperUtils.getImportType(importTypes, error.importTypeId)?.typeDescription;

    if (enrichedError.currentStatusId && statuses && 
      (StringUtils.isNullOrWhitespace(enrichedError.statusName)
      || StringUtils.isNullOrWhitespace(enrichedError.statusDescription))) {
      const status = LTHelperUtils.getCurrentStatus(statuses, enrichedError.currentStatusId);
      if (status) {
        enrichedError.statusName = status.statusName;
        enrichedError.statusDescription = status.statusDescription;
      }
    }

    if (enrichedError.errorCategoryId && categories && 
      (StringUtils.isNullOrWhitespace(enrichedError.categoryName)
      || StringUtils.isNullOrWhitespace(enrichedError.categoryDescription)
      || enrichedError.categoryPriority === null)) {
      const cat = LTHelperUtils.getErrorCategory(categories, enrichedError.errorCategoryId);
      if (cat) {
        enrichedError.categoryName = cat.categoryName;
        enrichedError.categoryDescription = cat.categoryDescription;
        enrichedError.categoryPriority = cat.categoryPriority;
      }
    }

    const dest = LTHelperUtils.getDestinationEndpoint(destinations, error.destinationEndpointId);
    if (dest){
      enrichedError.endpointName = dest.displayName ?? dest.endpointName;
    }

    enrichedError.integration = LTHelperUtils.getIntegration(integrations, error.integrationId);

    if (enrichedError.integration && enrichedError.integration.clientId !== null) {
      enrichedError.client = LTHelperUtils.getClientByLosTalkerClientId(clients, enrichedError.integration.clientId);
      enrichedError.clientInformationId = enrichedError?.client?.clientInformationId;
    }

    enrichedError.integrationDisplayName = LTHelperUtils.getIntegrationDisplayName(enrichedError);
    enrichedError.integrationName = enrichedError.integrationDisplayName;
    return enrichedError;
  }
  //#endregion

  constructor() {
    effect(() => {
      this.gridLoading.set(this.loading() ?? false);
    });

    effect(() => {
      const _ = this.data();
      untracked(() => this.selectedErrors.set([]));
    });

    effect(() => {
      const data = this.gridData();
      untracked(() => {
        if (!this.errorsGridReady()) return;

        if (this.hasStatusFilter()) {
          const allData = this.enrichedData();
          if (data.length === 0 && allData.length > 0) {
            this.toastService.toastCreate(
              'Filters produced no results. The filters have been reset', 'Warning',
              { keepAfterRouteChange: false, autoClose: true });
            this.statusSearchTerm.set(null);
            this.hasStatusFilter.set(false);
            this.filterValue = null;
            return;
          }
        }

        this.updateErrorsGrid();
      });
    });
  }

  public ngOnInit(): void {
    this.refresh();
    this.setupGrid();
  }

  //#region Grid Setup - Update Functions
  setupGrid(): void {
    const dataItems = this.gridData();

    let sorts = this.sorts();
    if (!sorts || sorts.length === 0) {
      sorts = [
        {
          field: 'receivedDate',
          order: 1,
        },
      ];
    }

    const singleSort = sorts[0];

    const settings = new GridSettings ({
      data: dataItems,
      fixedGrid: true,
      columns: this.errorColumns,
      loading: this.gridLoading,
      tableSize: 'small',
      sortMode: 'multiple',
      resizableColumns: true,
      reorderableColumns: true,
      multisort: sorts,
      sort: singleSort,
      dataKey: 'integrationErrorId',
      rowExpansion: {
        enabled: true,
        expandOnRowClick: false,
        allowMultiple: true,
        expandTemplate: 'expandedrow',
      },
      selection: {
        enabled: true,
        mode: 'multiple',
        selectionType: 'checkbox',
        showSelectAll: true,
      },
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: 'Errors',
        renderTemplates: false,
        header: 'exportHeader',
      },
      tableStyle: { 'min-width': 'auto' },
    });
    
    this.errorsGrid.set(settings);
    this.errorsGridReady.set(true);
  }

  updateErrorsGrid(): void {
    const dataItems = this.gridData();

    const updatedGrid = new GridSettings({
      ...this.errorsGrid(),
      data: dataItems,
      loading: this.gridLoading,
    });
    
    this.errorsGrid.set(updatedGrid);
  }

  //#endregion

  //#region Selection Handling
  public handleSelectionChange(event: IntegrationError[]){
    this.selectedErrors.set(event);
  }
  //#endregion

  //#region refresh

  public refresh(): void {
    this.selectedErrors.set([]);
  }
  //#endregion

  //#region Filtering

  //#region Status Filter
  filterValue: any;

  /** Clears Current Status Column Filter and updates grid */
  clearFilters() {
    this.statusSearchTerm.set(null);
    this.hasStatusFilter.set(false);
    this.filterValue = null;
  }

  /** Set Current Status Column Filter */
  onColumnStatusFilter(filterValue: StatusType | null, fieldName: string): void {
    // console.log('Column filter changed:', fieldName, filterValue);
    const previousFilter = this.statusSearchTerm();
    if (filterValue === previousFilter && filterValue !== null){
      this.clearFilters();
      return;
    }

    this.hasStatusFilter.set(true);
    this.statusSearchTerm.set(filterValue);

    this.filterValue = filterValue;
  }

  //#endregion Status Filter

  //#region Report Filter

  // computed variable - creates list of LTFilterRule from ReportErrorSearchFilter properties
  reportFilters = computed(() => {
    const filters: LTFilterRule[] = [];

    if (this.currentReportFilter() == null) return filters;

    const errorSearch = this.currentReportFilter();

    if (errorSearch.startReceivedDate != undefined) {
      filters.push({ field: 'receivedDate', operator: 'gte', value: errorSearch.startReceivedDate });
    }

    if (errorSearch.endReceivedDate != undefined) {
      filters.push({ field: 'receivedDate', operator: 'lte', value: errorSearch.endReceivedDate });
    }

    if (errorSearch.startInitialErrorDate != undefined) {
      filters.push({ field: 'initialErrorDate', operator: 'gte', value: errorSearch.startInitialErrorDate });
    }

    if (errorSearch.endInitialErrorDate != undefined) {
      filters.push({ field: 'initialErrorDate', operator: 'lte', value: errorSearch.endInitialErrorDate });
    }

    if (errorSearch.startResolutionDate != undefined) {
      filters.push({ field: 'resolutionDate', operator: 'gte', value: errorSearch.startResolutionDate });
    }

    if (errorSearch.endResolutionDate != undefined) {
      filters.push({ field: 'resolutionDate', operator: 'lte', value: errorSearch.endResolutionDate });
    }

    if (errorSearch.logMessage != undefined) {
      filters.push({ field: 'logMessage', operator: 'contains', value: errorSearch.logMessage });
    }

    if (errorSearch.errorCategoryId != undefined) {
      filters.push({ field: 'errorCategoryId', operator: 'eq', value: errorSearch.errorCategoryId });
    }

    if (errorSearch.currentStatusId != undefined) {
      filters.push({ field: 'currentStatusId', operator: 'eq', value: errorSearch.currentStatusId });
    }

    if (errorSearch.importTypeId != undefined) {
      filters.push({ field: 'importTypeId', operator: 'eq', value: errorSearch.importTypeId });
    }

    if (errorSearch.destinationEndpointId != undefined) {
      filters.push({ field: 'destinationEndpointId', operator: 'eq', value: errorSearch.destinationEndpointId });
    }

    return filters;
  });

  /**
   * Called from Integration Error Report Page to apply report filter to data
   * @param errorSearch 
   */
  filterByReportFilters(errorSearch: ReportErrorSearchFilter): void {
    this.selectedErrors.set([]);
    this.currentReportFilter.set(errorSearch);
  }
  //#endregion Report Filter

  //#endregion Filtering

  //#region dialog handling

  //#region Single Upload Dialog Handling
  triggerUpdateStatusMessage(error: IntegrationError, errorStatusName: string) {
    if (this.isUpdating()) {
      return;
    }

    this.selectedError.set(error);
    const selectedStatus = LTHelperUtils.getCurrentStatusByName(this.statuses(), errorStatusName);

    if (!selectedStatus || selectedStatus === null) {
      this.toastService.toastCreate('Invalid status selected', 'Warning');
      return;
    }

    this.statusUpdating.set(true);

    // only show dialog for resolved status update
    if (errorStatusName === 'Resolved') {
      // Open the new dialog component
      const dialogRef = LTErrorUpdateDialogComponent.open(
        this.dialog,
        this.selectedError(),
        selectedStatus,
      );

      // Handle dialog events using the new output signals
      dialogRef.componentInstance.errorUpdated.subscribe((updateData) => {
        this.handleSingleErrorUpdateFromDialog(updateData);
      });
      
      // Handle dialog events for dialog canceled signal
      dialogRef.componentInstance.dialogCanceled.subscribe(() => {
        this.statusUpdating.set(false);
        this.selectedError.set(null);
      });

      // Handle dialog closure
      dialogRef.afterClosed().subscribe(() => {
        this.statusUpdating.set(false);
      });
    } else {
      const updatedError: IntegrationError = {
        ...this.selectedError(), // Start with all existing properties
        currentStatusId: selectedStatus.currentStatusId,
        statusName: selectedStatus.statusName,
        resolutionComment: null,
        resolutionDate: null,
      };
      
      this.updateError(updatedError);
    }
  }

  handleErrorUpdated(updatedError: IntegrationError): void {
    this.errorUpdated.emit([updatedError]);
    this.toastService.toastCreate('Error updated successfully', 'Success');
  }

  private handleSingleErrorUpdateFromDialog(updateData: {
    error: IntegrationError;
    status: CurrentStatus;
    comment: string;
  }): void {
    this.isUpdating.set(true);

    // Create update payload
    const updatedError: IntegrationError = {
      ...updateData.error, // Start with all existing properties
      currentStatusId: updateData.status.currentStatusId,
      statusName: updateData.status.statusName,
      resolutionComment: updateData.comment,
      resolutionDate: updateData.status.statusName === 'Resolved' ? new Date() : updateData.error.resolutionDate,
    };

    this.updateError(updatedError);
  }

  // handle error update for service
  private updateError(updatedError: IntegrationError) {
    this.errorService.updateIntegrationError(updatedError)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.errorUpdated.emit([ updatedError ]);
          this.toastService.toastCreate('Error updated successfully', 'Success');
          this.isUpdating.set(false);
        },
        error: (error) => {
          this.toastService.toastCreate('Failed to update Integration Error' + error, 'Warning', {
            autoClose: false,
          });
          this.isUpdating.set(false);
        },
        complete: () => {
          this.selectedError.set(null);
          this.statusUpdating.set(false);
        },
      });
  }
  //#endregion

  //#region bulk update handling
  public openBulkUpdateDialog(): void {
    if (!this.hasSelectedErrors()){
      return;
    }

    const selectedErrorIds = this.selectedErrors().map(x => x.integrationErrorId);
    if (selectedErrorIds.length < 1) {
      return;
    }

    const dialogRef = LTErrorBulkUpdateDialogComponent.open(
      this.dialog,
      selectedErrorIds,
      this.statuses(),
    );

    // handle bulk update events
    dialogRef.componentInstance.bulkUpdate.subscribe((bulkUpdateData) => {
      this.handleBulkUpdate(bulkUpdateData);
    });

    dialogRef.componentInstance.dialogClosed.subscribe(() => {
      // handle dialog closure if needed
      // should it clear selected error list ?
    });
  }

  private handleBulkUpdate(bulkUpdateData: BulkErrorUpdateScope): void {
    this.isUpdating.set(true);

    this.errorService.bulkUpdateIntegrationErrors(bulkUpdateData)
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe({
      next: () => {
        this.updateExistingErrorsBulkChanges(bulkUpdateData);
        this.selectedErrors.set([]);
        this.toastService.toastCreate('Errors updated successfully', 'Success');
        this.isUpdating.set(false);
      },
      error: (error) => {
        this.toastService.toastCreate('Failed to update selected errors:' + error, 'Warning', {
            autoClose: false,
        });
      },
    });
  }

  public updateExistingErrorsBulkChanges(updateScope: BulkErrorUpdateScope): void {
    const currentErrors = this.data();
    const currentSelection = this.selectedErrors();
    const updatedErrors = [];

    updateScope.integrationErrorIdList.forEach(errorId => {
      // get from selected errors first, then fallback to all errors
      let currentError = currentSelection.find(d => d.integrationErrorId == errorId);
      if (!currentError) {
        currentError = currentErrors.find(d => d.integrationErrorId == errorId);
      }

      if (currentError){
        const updatedError = { ...currentError };

        if (updateScope.currentStatusId != undefined) {
          updatedError.currentStatusId = updateScope.currentStatusId;
          updatedError.statusName = updateScope.statusName;

          if (updateScope.statusName == 'Resolved') {
            updatedError.resolutionComment = updateScope.resolutionComment;
            if (updatedError.resolutionDate == undefined) {
              updatedError.resolutionDate = updateScope.resolutionDate;
            }
          }
        }

        if(updateScope.actionNeeded != undefined){
          updatedError.actionNeeded = new Boolean(updateScope.actionNeeded).valueOf();
        }

        if (updateScope.automatedResolution != undefined) {
          updatedError.automatedResolution = new Boolean(updateScope.automatedResolution).valueOf();
        }

        if (updateScope.criticalError != undefined) {
          updatedError.criticalError = new Boolean(updateScope.criticalError).valueOf();
        }

        if (updateScope.jiraStoryURL != undefined && updateScope.jiraStoryURL != '') {
          updatedError.jiraStoryURL = updateScope.jiraStoryURL;
        }

        updatedErrors.push(updatedError);
      }

    });

    // Emit to parent instead of trying to mutate input data
    if (updatedErrors.length > 0){
      this.errorUpdated.emit(updatedErrors);
    }

  }
  //#endregion

  //#endregion

  formatDate(dateValue: Date): string {
    let rc = undefined;
    if (dateValue != undefined) {
      const tmp = new Date(dateValue);
      rc = tmp.toLocaleString();
    }
    return rc;
  }
}
